/**
 * Default kiosk configuration
 * Based on Figma design specifications
 */

import { KioskUICustomization } from '../types/kiosk-types';

/**
 * Default kiosk configuration for 4-inch display
 * Matches Figma design: VPOSM 480x480
 *
 * Design specs:
 * - Background: #0A0A0A (dark)
 * - Card background: #262626
 * - Status colors: Available (#25EF89), Connected (#00BFFF), Occupied (#F79E1B), Unavailable (#707070)
 * - Logo: 95x48px, centered top
 * - Title: 26px Outfit font
 * - Cards: 450x114px with 15px spacing
 */
export const defaultKioskConfig: KioskUICustomization = {
  deviceType: 'kiosk',
  formFactor: '4inch',

  theme: {
    name: 'Default Kiosk Theme',
    colors: {
      // Background colors from Figma
      primaryBackground: '#0A0A0A',
      secondaryBackground: '#262626',

      // Text colors
      primaryText: '#FFFFFF',
      secondaryText: '#B8B8B8',
      mutedText: '#707070',

      // Accent colors
      accentPrimary: '#00BFFF',
      accentSecondary: '#25EF89',

      // Border color
      border: '#3A3A3A',

      // Status colors (from Figma connector statuses)
      statusSuccess: '#25EF89', // Available
      statusWarning: '#F79E1B', // Occupied
      statusError: '#707070', // Unavailable
      statusInfo: '#00BFFF', // Connected
    },

    // Optional gradient for header (disabled by default)
    headerGradient: {
      enabled: false,
      from: '#0A0A0A',
      to: '#1A1A1A',
      angle: 180,
    },

    // Optional gradient for footer
    footerGradient: {
      enabled: false,
      from: '#0A0A0A',
      to: '#262626',
      angle: 180,
    },

    // Default shadow for cards
    defaultShadow: {
      color: 'rgba(0, 0, 0, 0.5)',
      offsetX: 0,
      offsetY: 4,
      blur: 12,
      spread: 0,
    },

    // Active/hover shadow
    activeShadow: {
      color: 'rgba(0, 191, 255, 0.3)',
      offsetX: 0,
      offsetY: 4,
      blur: 16,
      spread: 2,
    },
  },

  layout: {
    // Header configuration (logo area)
    header: {
      height: 80,
      padding: {
        top: 16,
        right: 16,
        bottom: 16,
        left: 16,
      },
      logoSize: {
        width: 95,
        height: 48,
      },
      logoPosition: 'center',
    },

    // Content area (title + cards)
    content: {
      padding: {
        top: 0,
        right: 15,
        bottom: 0,
        left: 15,
      },
      titleTypography: {
        fontFamily: 'Outfit, sans-serif',
        fontSize: 26,
        fontWeight: 500,
        lineHeight: 1.2,
        letterSpacing: 0,
      },
      scrollbarVisible: false,
    },

    // Card configuration (connector cards)
    card: {
      spacing: 15,
      padding: {
        top: 16,
        right: 20,
        bottom: 16,
        left: 20,
      },
      minHeight: 114,
      maxHeight: 114,
    },

    // Footer configuration (payment icons)
    footer: {
      height: 60,
      padding: {
        top: 12,
        right: 16,
        bottom: 12,
        left: 16,
      },
      iconSize: {
        width: 40,
        height: 24,
      },
    },
  },

  components: {
    // Button styles
    button: {
      border: {
        width: 0,
        color: 'transparent',
        style: 'solid',
        radius: 8,
      },
      height: 44,
      padding: {
        horizontal: 20,
        vertical: 12,
      },
      fontSize: 16,
      fontWeight: 500,
    },

    // Card styles
    card: {
      border: {
        width: 1,
        color: '#3A3A3A',
        style: 'solid',
        radius: 12,
      },
      shadow: {
        color: 'rgba(0, 0, 0, 0.5)',
        offsetX: 0,
        offsetY: 4,
        blur: 12,
        spread: 0,
      },
      hoverShadow: {
        color: 'rgba(0, 191, 255, 0.3)',
        offsetX: 0,
        offsetY: 4,
        blur: 16,
        spread: 2,
      },
      hoverEffect: true,
      transitionDuration: 200,
    },

    // Badge styles (status indicators)
    badge: {
      borderRadius: 16,
      padding: {
        horizontal: 12,
        vertical: 4,
      },
      fontSize: 12,
      fontWeight: 600,
    },

    // Icon styles
    icon: {
      defaultSize: 32,
      smallSize: 20,
      largeSize: 48,
      opacity: 1,
    },
  },

  // Icon placeholders (will be populated with actual assets)
  // Fallback URLs point to Figma-exported assets
  icons: {
    logo: {
      assetId: '',
      s3Url: '',
      localFileName: '',
      fallbackUrl: '/figma-assets/kiosk/nayax-energy-logo.svg',
    },
    chademo: {
      assetId: '',
      s3Url: '',
      localFileName: '',
      fallbackUrl: '/figma-assets/kiosk/chademo-icon.svg',
    },
    ccs: {
      assetId: '',
      s3Url: '',
      localFileName: '',
      fallbackUrl: '/figma-assets/kiosk/chademo-icon.svg', // Using same for now
    },
    type2: {
      assetId: '',
      s3Url: '',
      localFileName: '',
      fallbackUrl: '/figma-assets/kiosk/chademo-icon.svg', // Using same for now
    },
    visa: {
      assetId: '',
      s3Url: '',
      localFileName: '',
      fallbackUrl: '', // Part of payment-logos composite
    },
    mastercard: {
      assetId: '',
      s3Url: '',
      localFileName: '',
      fallbackUrl: '', // Part of payment-logos composite
    },
    applePay: {
      assetId: '',
      s3Url: '',
      localFileName: '',
      fallbackUrl: '', // Part of payment-logos composite
    },
    googlePay: {
      assetId: '',
      s3Url: '',
      localFileName: '',
      fallbackUrl: '', // Part of payment-logos composite
    },
    menuIcon: {
      assetId: '',
      s3Url: '',
      localFileName: '',
      fallbackUrl: '/figma-assets/kiosk/menu-icon.svg',
    },
    languageIcon: {
      assetId: '',
      s3Url: '',
      localFileName: '',
      fallbackUrl: '/figma-assets/kiosk/language-icon.svg',
    },
    // Idle screen media (optional - no defaults)
    media4IdleScreen: undefined,
    media5IdleScreen: undefined,
  },

  metadata: {
    version: '1.0.0',
    createdAt: new Date().toISOString(),
    modifiedAt: new Date().toISOString(),
    description: 'Default kiosk configuration based on Figma design',
  },
};

/**
 * Default configuration for 5-inch display
 * Larger screen with adjusted dimensions
 */
export const defaultKiosk5InchConfig: KioskUICustomization = {
  ...defaultKioskConfig,
  formFactor: '5inch',

  layout: {
    ...defaultKioskConfig.layout,

    // Larger header for 5" screen
    header: {
      ...defaultKioskConfig.layout.header,
      height: 120,
      logoSize: {
        width: 140,
        height: 70,
      },
    },

    // Adjusted content padding
    content: {
      ...defaultKioskConfig.layout.content,
      padding: {
        top: 0,
        right: 30,
        bottom: 0,
        left: 30,
      },
      titleTypography: {
        ...defaultKioskConfig.layout.content.titleTypography,
        fontSize: 38, // Larger title for 5"
      },
    },

    // Larger cards for 5" screen (660x200px)
    card: {
      spacing: 20,
      padding: {
        top: 24,
        right: 30,
        bottom: 24,
        left: 30,
      },
      minHeight: 200,
      maxHeight: 200,
    },

    // Larger footer
    footer: {
      height: 80,
      padding: {
        top: 16,
        right: 30,
        bottom: 16,
        left: 30,
      },
      iconSize: {
        width: 50,
        height: 30,
      },
    },
  },

  components: {
    ...defaultKioskConfig.components,

    // Larger icons for 5" screen
    icon: {
      defaultSize: 48,
      smallSize: 28,
      largeSize: 64,
      opacity: 1,
    },

    // Larger badges
    badge: {
      ...defaultKioskConfig.components.badge,
      fontSize: 14,
      padding: {
        horizontal: 16,
        vertical: 6,
      },
    },
  },

  metadata: {
    ...defaultKioskConfig.metadata,
    description: 'Default kiosk configuration for 5-inch display',
  },
};

/**
 * Get default configuration for form factor
 *
 * @param formFactor - Form factor ID
 * @returns Default configuration
 */
export function getDefaultKioskConfig(formFactor: '4inch' | '5inch'): KioskUICustomization {
  return formFactor === '5inch' ? defaultKiosk5InchConfig : defaultKioskConfig;
}
